<script lang="ts">
  /**
   * Join a game by its code: a sentence saying what to type, the code boxes
   * (`CodeBoxes`, one real input), Paste, the lookup button, and, once the
   * game says what the code is, a confirmation card (`confirm`) with Join.
   *
   * The game owns everything a code means: its alphabet (`allowed`), how a
   * look-alike folds (`normalise`, "O" to "Q") and which part of a pasted
   * link is the code (`extract`: `/join/CODE`, `#/join/CODE`). `foldCode` /
   * `extractCode` are the same steps as plain functions. The template never
   * asks a server anything: `onlookup` is the game's, and the game answers by
   * setting `error` or `confirm`.
   *
   * - **`code`** is bindable: the boxes write it, Paste writes it, the game
   *   may set it (a link, an invite). Paste reads the clipboard and hands the
   *   text to the boxes, which extract and fold it; no permission, nothing.
   * - **`lookup`** is the button under the boxes (Enter in the boxes too); it
   *   is disabled until `codeLength` characters are in.
   * - **`error`** is why the last lookup found nothing, announced at once.
   * - **While `lookup.busy`** (since 1.3.0) the code being asked about
   *   cannot change under the question: the boxes are held (`CodeBoxes`'s
   *   `busy`: read-only and `aria-disabled`, so a focused input keeps the
   *   focus), Paste is disabled (a lookup starts from Join or Enter in the
   *   boxes, never from Paste, so Paste never has the focus then), and a
   *   clipboard read that lands meanwhile is ignored.
   * - **A Paste that brings nothing** (since 1.3.0): the clipboard was
   *   refused, empty, or held no character the boxes keep (`foldCode` of it
   *   is empty). The code stays as it was, `onpastefail` is called, and
   *   `pasteError`, when given, is said in the error line (in place of
   *   `error`) until the code changes, a Paste works, a lookup starts or
   *   `error` changes. Without `pasteError` nothing is said. (In 1.2.0 such
   *   a Paste emptied the boxes.)
   * - **`extra`** sits between the button and the `hint`: invites for this
   *   player, recent rooms.
   * - **`confirm`** (a `JoinConfirm`, or `null` while there is none) is a
   *   centred dialog: its `title`, the found game (`details`), the seat
   *   question (`role`: Player or Spectator, or the name a seat needs), an
   *   error line, Join, and a quiet way out. Back and Escape close it.
   *
   * **`mode: 'link'`** (since 1.5.0): confirm only, for an invite that
   * arrived as a link (a token, no code to type). No code boxes, no Paste, no
   * lookup button: the page holds `prompt` (optional: "Robin invited you"),
   * `error` (the invite is gone), the confirmation card itself (`confirm`,
   * drawn in the page rather than as a dialog over an empty one, with the
   * same parts, test ids and `autofocus` on Join), `extra` and `hint`.
   * While the game is still reading the link it passes `confirm: null`
   * and a `prompt` that says so. The way out (and the card's `dismiss`)
   * are the game's: `onclose` and `confirm.onclose`. When `confirm`
   * arrives (from `null` to a card), the focus goes to its Join. The code
   * props are not used in this mode.
   *
   * The props stay one flat interface, as 1.3.0's (a game's own props may
   * `extend` them, and a wrapper may spread them): the code mode's props
   * (`prompt`, `codeLength`, `codeLabel`, `onlookup`, `lookup`) are
   * optional in the type, and a missing one (or one passed in the link mode)
   * is said once in the console. For the compiler's check, intersect them
   * with `JoinCodeProps` or `JoinLinkProps` (`pageTypes.ts`).
   *
   * `frame` (`pageTypes.ts`): `page` (the default) is a `FullPage` in the
   * `chevron` dress that owns its Back; `screen` is the game's own routed
   * screen under a back chevron, content 30rem wide from 1024px.
   *
   * Test ids: `testid` on the dialog or screen; `testids.back` on the way
   * out; `testids.code` on the input (default `ui-join-code`),
   * `testids.paste` (default `ui-join-paste`), `testids.error` (default
   * `ui-join-error`); `lookup.testid` (default `ui-join-lookup`); the card's
   * `confirm.testid` (default `ui-join-confirm`), `confirm.errorTestid`
   * (default `ui-join-confirm-error`), `confirm.join.testid` (default
   * `ui-join-confirm-join`) and `confirm.dismiss.testid` (default
   * `ui-join-confirm-dismiss`).
   */
  import { type Snippet, untrack } from 'svelte';

  import { foldCode } from '../code.js';
  import CodeBoxes from '../components/CodeBoxes.svelte';
  import { shellText } from '../i18n.svelte.js';
  import Icon from '../components/Icon.svelte';
  import Note from '../components/Note.svelte';
  import PillButton from '../components/PillButton.svelte';
  import Sheet from '../components/Sheet.svelte';
  import PageFrame from './PageFrame.svelte';
  import type { JoinConfirm, PageDress, PageFrame as Frame } from './pageTypes.js';
  import type { TemplateAction } from '../types.js';

  interface Props {
    /** The page's title and accessible name ("Join a game"). */
    readonly title: string;
    readonly onclose: () => void;
    /** `code` (the default): the boxes, Paste and the lookup; `link`: confirm only (since 1.5.0). */
    readonly mode?: 'code' | 'link' | undefined;
    /** What to type ("Type the 5-letter code your friend sent you."); in `link`, a line over the card. Required in `code`. */
    readonly prompt?: string | undefined;
    /** How many characters a code has (4 or 5). Required in `code`. */
    readonly codeLength?: number | undefined;
    /** The input's accessible name ("Game code, 5 letters"). Required in `code`. */
    readonly codeLabel?: string | undefined;
    /** Asks the game what the code is. Also Enter in the boxes, once the code is whole. Required in `code`. */
    readonly onlookup?: (() => void) | undefined;
    /** The lookup button: its label ("Join"), `busy` while the game asks. `onclick` is `onlookup`. Required in `code`. */
    readonly lookup?: Omit<TemplateAction, 'onclick'> | undefined;
    /** The code so far. Bindable. */
    code?: string;
    /** Which single characters survive; letters and digits by default. */
    readonly allowed?: RegExp | undefined;
    /** Folds look-alikes after upper-casing, before `allowed` filters. */
    readonly normalise?: ((code: string) => string) | undefined;
    /** The part of a paste that can be the code. Default: a link's last path segment. */
    readonly extract?: ((raw: string) => string) | undefined;
    /** Paste's label (default the shell's `paste`, "Paste"); `false` draws no Paste. */
    readonly pasteLabel?: string | false;
    /** Why the last lookup found nothing, as a sentence. */
    readonly error?: string | null | undefined;
    /**
     * Paste brought nothing: the clipboard was refused, empty, or held no
     * character the boxes keep. Called after the attempt; the code is left
     * as it was. Since 1.3.0.
     */
    readonly onpastefail?: (() => void) | undefined;
    /**
     * What the error line says after such a Paste ("Nothing to paste. Copy
     * the code, then try again."), until the code changes or a Paste works.
     * Absent: nothing is said. Since 1.3.0.
     */
    readonly pasteError?: string | undefined;
    /** A line at the bottom ("A link from a friend opens this screen with the code filled in."). */
    readonly hint?: string | undefined;
    /** Between the button and the hint: invites, recent rooms. */
    readonly extra?: Snippet | undefined;
    /** The found game, or `null` while there is none. */
    readonly confirm?: JoinConfirm | null | undefined;
    /** Focus the boxes on arrival (not when a link already filled them); `false` leaves the focus to the screen's title. */
    readonly autofocus?: boolean;
    readonly open?: boolean;
    readonly frame?: Frame;
    readonly dress?: PageDress;
    readonly leaveLabel?: string | undefined;
    readonly testid?: string | undefined;
    readonly testids?:
      | {
          readonly back?: string;
          readonly code?: string;
          readonly paste?: string;
          readonly error?: string;
        }
      | undefined;
  }

  let {
    title,
    onclose,
    mode = 'code',
    prompt,
    code = $bindable(''),
    codeLength,
    codeLabel,
    allowed,
    normalise,
    extract,
    onlookup,
    lookup,
    pasteLabel,
    error,
    onpastefail,
    pasteError,
    hint,
    extra,
    confirm = null,
    autofocus = true,
    open = true,
    frame = 'page',
    dress = 'chevron',
    leaveLabel,
    testid,
    testids,
  }: Props = $props();

  const uid = $props.id();
  const link = $derived(mode === 'link');
  const length = $derived(codeLength ?? 0);
  const complete = $derived(!link && code.length === length);
  const busy = $derived(lookup?.busy === true);

  // The flat props allow a combination the template cannot draw well; the
  // game is told once (the strict types, JoinCodeProps / JoinLinkProps, are
  // the compile-time check).
  let warned = false;
  $effect(() => {
    if (warned) return;
    const codeProps = [codeLength, codeLabel, onlookup, lookup];
    if (!link && (prompt === undefined || codeProps.some((value) => value === undefined))) {
      warned = true;
      console.warn(
        'JoinTemplate: the code mode needs prompt, codeLength, codeLabel, onlookup and lookup.',
      );
    } else if (link && codeProps.some((value) => value !== undefined)) {
      warned = true;
      console.warn(
        'JoinTemplate: the link mode draws no code boxes; codeLength, codeLabel, onlookup and lookup are ignored.',
      );
    }
  });

  // Link mode: a card that arrives (from null) takes the focus on its Join,
  // so the player hears what was found and can answer at once.
  let cardSection = $state<HTMLElement | undefined>();
  let hadCard = untrack(() => confirm !== null);
  $effect(() => {
    if (confirm === null) {
      hadCard = false;
      return;
    }
    if (hadCard) return;
    if (!link) {
      hadCard = true;
      return;
    }
    // Wait for the card to be in the page.
    if (cardSection === undefined) return;
    hadCard = true;
    cardSection.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  });

  // The last Paste brought nothing. A change to the code or to the game's
  // `error` clears it, and so does a lookup (`submit`): it is never left
  // standing over a newer answer.
  let pasteFailed = $state(false);
  $effect(() => {
    void code;
    void error;
    pasteFailed = false;
  });
  const errorLine = $derived(
    pasteFailed && pasteError !== undefined ? pasteError : (error ?? null),
  );

  function submit(): void {
    if (!complete || lookup?.disabled === true || busy) return;
    pasteFailed = false;
    onlookup?.();
  }

  async function paste(): Promise<void> {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      // No clipboard permission, or no clipboard: the boxes stay as they are.
      if (!busy) failPaste();
      return;
    }
    // A lookup started while the clipboard was read: the code it asks about stays.
    if (busy) return;
    // Nothing the boxes would keep (the same steps they take): leave the code be.
    if (foldCode(text, { length, allowed, extract, normalise }) === '') {
      failPaste();
      return;
    }
    // The boxes extract and fold whatever is set from outside.
    code = text;
    pasteFailed = false;
  }

  function failPaste(): void {
    pasteFailed = true;
    onpastefail?.();
  }
</script>

{#snippet pasteIcon()}<Icon name="paste" size={18} />{/snippet}

{#snippet card(found: JoinConfirm)}
  {@render found.details()}
  {#if found.role !== undefined}{@render found.role()}{/if}
  {#if found.error !== undefined && found.error !== null}
    <Note tone="error" testid={found.errorTestid ?? 'ui-join-confirm-error'}>{found.error}</Note>
  {/if}
  <PillButton
    label={found.join.label}
    size="lg"
    disabled={found.join.disabled === true}
    busy={found.join.busy === true}
    icon={found.join.icon}
    autofocus
    testid={found.join.testid ?? 'ui-join-confirm-join'}
    onclick={found.join.onclick}
  />
  <PillButton
    label={found.dismiss.label}
    variant="plain"
    testid={found.dismiss.testid ?? 'ui-join-confirm-dismiss'}
    onclick={() => found.onclose()}
  />
{/snippet}

<PageFrame
  {frame}
  {open}
  {title}
  {onclose}
  {dress}
  {leaveLabel}
  measure="narrow"
  focusTitle={link || !autofocus}
  {testid}
  {testids}
>
  <div class="ui-join" data-mode={mode}>
    {#if prompt !== undefined}
      <p class="ui-join-prompt">{prompt}</p>
    {/if}
    {#if link}
      {#if errorLine !== null}
        <Note tone="error" testid={testids?.error ?? 'ui-join-error'}>{errorLine}</Note>
      {/if}
      {#if confirm !== null}
        <section
          bind:this={cardSection}
          class="ui-join-card ui-join-card--page"
          aria-labelledby="{uid}-card"
          data-testid={confirm.testid ?? 'ui-join-confirm'}
        >
          <h2 class="ui-join-card-title" id="{uid}-card">{confirm.title}</h2>
          {@render card(confirm)}
        </section>
      {/if}
    {:else}
      <CodeBoxes
        bind:value={code}
        {length}
        label={codeLabel ?? ''}
        {...allowed !== undefined ? { allowed } : {}}
        {extract}
        {normalise}
        {autofocus}
        {busy}
        testid={testids?.code ?? 'ui-join-code'}
        onsubmit={submit}
      />
      {#if pasteLabel !== false}
        <div class="ui-join-paste">
          <PillButton
            label={pasteLabel ?? shellText('paste')}
            variant="plain"
            icon={pasteIcon}
            disabled={busy}
            testid={testids?.paste ?? 'ui-join-paste'}
            onclick={() => void paste()}
          />
        </div>
      {/if}
      {#if errorLine !== null}
        <Note tone="error" testid={testids?.error ?? 'ui-join-error'}>{errorLine}</Note>
      {/if}
      {#if lookup !== undefined}
        <PillButton
          label={lookup.label}
          size="lg"
          disabled={!complete || lookup.disabled === true}
          {busy}
          icon={lookup.icon}
          testid={lookup.testid ?? 'ui-join-lookup'}
          onclick={submit}
        />
      {/if}
    {/if}
    {#if extra !== undefined}{@render extra()}{/if}
    {#if hint !== undefined}
      <Note centered weight="bold" size="sm">{hint}</Note>
    {/if}
  </div>
</PageFrame>

{#if confirm !== null && !link}
  <Sheet
    open={true}
    title={confirm.title}
    variant="dialog"
    testid={confirm.testid ?? 'ui-join-confirm'}
    onclose={() => confirm?.onclose()}
  >
    <div class="ui-join-card">
      {@render card(confirm)}
    </div>
  </Sheet>
{/if}

<style>
  .ui-join {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .ui-join-prompt {
    margin: 0;
    font: var(--ui-text-body);
    font-weight: 700;
    line-height: 1.4;
    color: var(--ui-ink);
  }

  .ui-join-paste {
    display: flex;
    justify-content: center;
  }

  .ui-join-card {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
  }

  /* Confirm only (since 1.5.0): the card in the page, on the card step. */
  .ui-join-card--page {
    padding: var(--ui-space-4);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-card);
  }

  .ui-join-card-title {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }
</style>
